import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import {
  searchParams,
  statusParams,
  type ObjectStatus,
  type UsageParam,
} from '@/services/core/listSearch'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface RulesetRef {
  id: string
  name: string
}

export interface EarningsAccount {
  id: string
  name: string
  objectState: string
  labels: string[]
  address: {
    addressLine1: string
    addressLine2: string
    city: string
    state: string
    postCode: string
    country: string
  }
  countries: string[]
  filterTeams: string[]
  filterTeamsType: string
  ownerTeamId: string
  ownerTeamName: string
  description: string
  externalReference: string
  revenueShare: number
  directDealRevenueShare: number
  blocklist: string
  politicalAdvertising: string
  fullPageSizes: Record<string, string>
  siteRuleset: RulesetRef | null
  targetingRulesets: RulesetRef[]
  sizeRulesets: RulesetRef[]
  layoutRulesets: RulesetRef[]
  validationWarnings: ValidationWarning[]
  createTime: string
  updateTime: string
}

export interface EarningsAccountInput {
  name: string
  labels?: string[]
  address?: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    state?: string
    postCode?: string
    country?: string
  }
  countries?: string[]
  filterTeams?: { id: string }[]
  filterTeamsType?: string
  ownerTeamId?: string
  description?: string
  externalReference?: string
  revenueShare?: number
  directDealRevenueShare?: number
  blocklist?: { id: string } | null
  politicalAdvertising?: string
  fullPageSizes?: Record<string, string>
  siteRuleset?: { id: string } | null
  targetingRulesets?: { id: string }[]
  sizeRulesets?: { id: string }[]
  layoutRulesets?: { id: string }[]
  objectState?: string
}

function mapRulesetRef(r: Record<string, unknown>): RulesetRef {
  return { id: (r.id as string) ?? '', name: (r.name as string) ?? '' }
}

function mapEarningsAccount(r: Record<string, unknown>): EarningsAccount {
  const address = r.address as Record<string, unknown> | undefined
  const ownerTeam = r.team as Record<string, unknown> | undefined
  const sr = r.siteRuleset as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    labels: (r.labels as string[]) ?? [],
    address: {
      addressLine1: (address?.addressLine1 as string) ?? '',
      addressLine2: (address?.addressLine2 as string) ?? '',
      city: (address?.city as string) ?? '',
      state: (address?.state as string) ?? '',
      postCode: (address?.postCode as string) ?? '',
      country: (address?.country as string) ?? '',
    },
    countries: (r.countries as string[]) ?? [],
    filterTeams: ((r.filterTeams as Array<Record<string, unknown>>) ?? []).map(t => (t.id as string) ?? ''),
    filterTeamsType: (r.filterTeamsType as string) ?? '',
    ownerTeamId: (ownerTeam?.id as string) ?? '',
    ownerTeamName: (ownerTeam?.name as string) ?? '',
    description: (r.description as string) ?? '',
    externalReference: (r.externalReference as string) ?? '',
    revenueShare: (r.revenueShare as number) ?? 0,
    directDealRevenueShare: (r.directDealRevenueShare as number) ?? 0,
    blocklist: r.blocklist
      ? typeof r.blocklist === 'string' ? r.blocklist : ((r.blocklist as Record<string, unknown>)?.id as string) ?? ''
      : '',
    politicalAdvertising: (r.politicalAdvertising as string) ?? '',
    fullPageSizes: (r.fullPageSizes as Record<string, string>) ?? {},
    siteRuleset: sr ? mapRulesetRef(sr) : null,
    targetingRulesets: ((r.targetingRulesets as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    sizeRulesets: ((r.sizeRulesets as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    layoutRulesets: ((r.layoutRulesets as Record<string, unknown>[]) ?? []).map(mapRulesetRef),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
  }
}

/**
 * The list endpoint caps a page at 100 rows, so the full list is walked page by
 * page — a network with more publishers than that would otherwise show only an
 * alphabetical slice of them.
 */
const PAGE_SIZE = 100
const MAX_PAGES = 25

export const EarningsAccountsApi = {
  getAll: async (search?: string, status?: ObjectStatus): Promise<EarningsAccount[]> => {
    const all: EarningsAccount[] = []
    for (let page = 1; page <= MAX_PAGES; page++) {
      const params = new URLSearchParams({
        ...searchParams('earningsAccounts', search),
        concise: 'true',
        context: getContext(),
        ...statusParams(status),
        includeHidden: 'false',
        orderBy: 'name',
        orderByDirection: 'DESCENDING',
        page: String(page),
        pageSize: String(PAGE_SIZE),
      })
      const res = await apiFetch(`${API_BASE}/earningsaccounts?${params}`, {
        headers: JSON_HEADERS,
      })
      if (!res.ok) await throwApiError(res, 'Failed to fetch publishers')
      const results = toArray(await res.json())
      all.push(...results.map(mapEarningsAccount))
      if (results.length < PAGE_SIZE) break
    }
    return all
  },

  /** Everything using the given blocklist or ruleset. */
  getByUsage: async (usageParam: UsageParam, resourceId: string): Promise<EarningsAccount[]> => {
    const params = new URLSearchParams({
      concise: 'true',
      context: getContext(),
      [usageParam]: resourceId,
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'name',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/earningsaccounts?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch publishers')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapEarningsAccount)
  },

  getById: async (id: string): Promise<EarningsAccount> => {
    const res = await apiFetch(`${API_BASE}/earningsaccounts/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch publisher')
    return mapEarningsAccount(await res.json())
  },

  create: async (id: string, input: EarningsAccountInput): Promise<EarningsAccount> => {
    const { ownerTeamId, ...rest } = input
    const body: Record<string, unknown> = { id, ...rest }
    if (ownerTeamId) body.team = { id: ownerTeamId }
    const res = await apiFetch(`${API_BASE}/earningsaccounts/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create publisher')
    return mapEarningsAccount(await res.json())
  },

  update: async (id: string, input: EarningsAccountInput): Promise<EarningsAccount> => {
    const { ownerTeamId, ...rest } = input
    const body: Record<string, unknown> = { ...rest }
    if (ownerTeamId) body.team = { id: ownerTeamId }
    const res = await apiFetch(`${API_BASE}/earningsaccounts/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save publisher')
    return mapEarningsAccount(await res.json())
  },
}
